import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useRequestStore, useRecurringStore } from '../../stores/root_store';
import { useMessageAttachments } from '../../hooks/useMessageAttachments';
import { MessageComposerAttachments } from '../../components/MessageComposerAttachments';
import { useAccountSummary } from '../../hooks/useAccountSummary';
import { AccountSummaryLine, OutOfTasksBanner } from '../../components/AccountSummary';

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const RequestCreatePage = observer(() => {
  const requestStore = useRequestStore();
  const recurringStore = useRecurringStore();
  const navigate = useNavigate();
  const { summary, outOfTasks } = useAccountSummary();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { attachments, addFiles, removeAttachment, clearAttachments, uploading, associateAll } = useMessageAttachments();

  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [weekday, setWeekday] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (outOfTasks) {
      return;
    }

    if (!content.trim()) {
      setError('Please describe what you need help with');
      return;
    }

    setSubmitting(true);

    if (isRecurring) {
      const response = await recurringStore.createRecurring({
        content: content.trim(),
        title: title.trim() || undefined,
        frequency,
        ...(frequency === 'weekly' && { weekday }),
        ...(frequency === 'monthly' && { day_of_month: dayOfMonth }),
      });

      if (response?.request) {
        if (attachments.some(a => a.status === 'complete')) {
          await associateAll([{ type: 'request', uuid: response.request.uuid }]);
        }
        clearAttachments();
        navigate(`/request/${response.request.key}`);
        return;
      }
      setError(recurringStore.error || 'Failed to create recurring request');
    } else {
      const request = await requestStore.createRequest({
        content: content.trim(),
        title: title.trim() || undefined,
      });

      if (request) {
        if (attachments.some(a => a.status === 'complete')) {
          await associateAll([{ type: 'request', uuid: request.uuid }]);
        }
        clearAttachments();
        navigate(`/request/${request.key}`);
        return;
      }
      setError(requestStore.error || 'Failed to create request');
    }

    setSubmitting(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Create New Request
      </Typography>

      {summary && outOfTasks && <OutOfTasksBanner summary={summary} />}

      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Title (optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your request"
              margin="normal"
            />

            <TextField
              fullWidth
              label="What do you need help with?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Describe your request in detail. The more information you provide, the better we can help you."
              multiline
              rows={6}
              margin="normal"
              required
            />

            <MessageComposerAttachments
              attachments={attachments}
              onAddFiles={addFiles}
              onRemove={removeAttachment}
              disabled={submitting}
            />

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                  />
                }
                label="Make this a recurring request"
              />

              <Collapse in={isRecurring}>
                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={frequency}
                      label="Frequency"
                      onChange={(e) => setFrequency(e.target.value)}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekdays">Weekdays (Mon-Fri)</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>

                  {frequency === 'weekly' && (
                    <FormControl sx={{ minWidth: 160 }}>
                      <InputLabel>Day of Week</InputLabel>
                      <Select
                        value={weekday}
                        label="Day of Week"
                        onChange={(e) => setWeekday(Number(e.target.value))}
                      >
                        {DAY_NAMES.map((name, i) => (
                          <MenuItem key={i} value={i}>{name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {frequency === 'monthly' && (
                    <FormControl sx={{ minWidth: 160 }}>
                      <InputLabel>Day of Month</InputLabel>
                      <Select
                        value={dayOfMonth}
                        label="Day of Month"
                        onChange={(e) => setDayOfMonth(Number(e.target.value))}
                      >
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <MenuItem key={day} value={day}>{day}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  Your first request will be created now. Future requests will be
                  automatically submitted on the selected schedule.
                </Alert>
              </Collapse>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || uploading || !content.trim() || outOfTasks}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isRecurring ? (
                  'Submit Recurring Request'
                ) : (
                  'Submit Request'
                )}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/dashboard')}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Box>
          </Box>

          {summary && (
            <Box sx={{ mt: 3 }}>
              <AccountSummaryLine summary={summary} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});
